/**
 * GatheringModule - Professional resource harvesting system.
 * Handles targeted right-click gathering, range checks (<= 2.5 tiles),
 * cast progression, tool sounds, resource yields, and dynamic node respawns.
 */

import { EntityState } from '../../core/Types';
import { EntityRegistry } from '../../core/EntityRegistry';
import { EventBus } from '../../core/EventBus';
import { addItemToInventory, practiceSelfSkill } from '../character/CharacterSystem';

export interface GatheringYield {
  itemKey: string;
  nameKr: string;
  amount: number;
  icon: string;
}

export class GatheringModule {
  private registry: EntityRegistry;
  private respawnQueue: Array<{
    nodeId: string;
    respawnTimer: number;
    maxHp: number;
  }> = [];

  constructor(registry: EntityRegistry) {
    this.registry = registry;
  }

  /**
   * Start gathering action when player right-clicks a resource node.
   */
  public startGathering(harvester: EntityState, targetNode: EntityState): boolean {
    if (harvester.isDead) return false;

    // Check node category
    if (targetNode.category !== 'resource' || !targetNode.resourceType) {
      return false;
    }

    // Check if node is depleted
    if (targetNode.isDead || targetNode.resources.hp <= 0) {
      EventBus.emit('LOG_EMITTED', {
        id: Math.random().toString(),
        timestamp: performance.now(),
        text: `⚠️ [${targetNode.name}] 자원이 이미 고갈되었습니다. 곧 재생성됩니다.`,
        category: 'system',
      });
      return false;
    }

    // Range check (2.5 tiles max)
    const dist = this.registry.getDistance(harvester, targetNode);
    if (dist > 2.5) {
      EventBus.emit('LOG_EMITTED', {
        id: Math.random().toString(),
        timestamp: performance.now(),
        text: `⚠️ [${targetNode.name}] 거리가 너무 멉니다! (${dist.toFixed(1)}m / 2.5m 이내로 접근하세요)`,
        category: 'system',
      });
      return false;
    }

    // Determine gathering parameters based on type
    let castTime = 2.0;
    let actionName = '자원 채집 중...';

    if (targetNode.resourceType === 'tree') {
      castTime = 2.0;
      actionName = '🌲 고목나무 벌목 중...';
    } else if (targetNode.resourceType === 'rock_ore') {
      castTime = 2.4;
      actionName = '⛏️ 철광석 채굴 중...';
    } else if (targetNode.resourceType === 'herb_plant') {
      castTime = 1.4;
      actionName = '🌿 치유초 채집 중...';
    } else if (targetNode.resourceType === 'campfire') {
      castTime = 1.5;
      actionName = '🔥 모닥불에서 요리 및 휴식 중...';
    }

    // Start gathering cast
    harvester.cast = {
      skillId: -99, // Special gathering ID
      skillName: actionName,
      targetId: targetNode.id,
      castTime,
      progress: 0,
    };

    EventBus.emit('CAST_STARTED', {
      entityId: harvester.id,
      spellName: actionName,
      castTime,
      targetId: targetNode.id,
    });

    EventBus.emit('LOG_EMITTED', {
      id: Math.random().toString(),
      timestamp: performance.now(),
      text: `🛠️ [${harvester.name}] 이 [${targetNode.name}] 채집을 시작했습니다.`,
      category: 'crafting',
    });

    return true;
  }

  /**
   * Update gathering casts and handle node respawns
   */
  public update(dt: number): void {
    const allEntities = this.registry.getAll();

    // 1. Update harvesting casts
    for (const ent of allEntities) {
      if (ent.cast && ent.cast.skillId === -99) {
        ent.cast.progress += dt;

        // Cancel if target node died or vanished
        const targetNode = ent.cast.targetId ? this.registry.get(ent.cast.targetId) : null;
        if (!targetNode || targetNode.isDead) {
          ent.cast = null;
          continue;
        }

        // Cancel if walked too far during cast
        if (this.registry.getDistance(ent, targetNode) > 3.0) {
          ent.cast = null;
          EventBus.emit('CAST_INTERRUPTED', {
            entityId: ent.id,
            entityName: ent.name,
            spellName: '채집 중단',
            reason: 'movement',
          });
          continue;
        }

        // Completion check
        if (ent.cast.progress >= ent.cast.castTime) {
          this.completeGathering(ent, targetNode);
          ent.cast = null;
        }
      }
    }

    // 2. Update node respawn queue & growth animation
    for (const ent of allEntities) {
      if (ent.growAnimTimer && ent.growAnimTimer > 0) {
        ent.growAnimTimer -= dt;
      }
    }

    this.respawnQueue = this.respawnQueue.filter((item) => {
      item.respawnTimer -= dt;
      const node = this.registry.get(item.nodeId);
      if (node) {
        node.respawnRemaining = Math.max(0, item.respawnTimer);
      }

      if (item.respawnTimer <= 0) {
        if (node) {
          node.isDead = false;
          node.resources.hp = item.maxHp;
          node.respawnRemaining = 0;
          node.growAnimTimer = 0.8; // 0.8s sprout bounce animation

          EventBus.emit('RESOURCE_RESPAWNED', {
            nodeId: node.id,
            x: node.x,
            y: node.y,
            resourceType: node.resourceType || 'tree',
            name: node.name,
          });

          EventBus.emit('LOG_EMITTED', {
            id: Math.random().toString(),
            timestamp: performance.now(),
            text: `🌱 [${node.name}] 자원이 다시 풍성하게 자라났습니다!`,
            category: 'system',
          });
        }
        return false;
      }
      return true;
    });
  }

  /**
   * Finalize gathering: Deduct durability, grant items, emit events
   */
  private completeGathering(harvester: EntityState, node: EntityState): void {
    const rType = node.resourceType || 'tree';
    let yieldInfo: GatheringYield = {
      itemKey: 'wood_log',
      nameKr: '단단한 통나무',
      amount: 2,
      icon: '🪵',
    };

    if (rType === 'tree') {
      yieldInfo = { itemKey: 'wood_log', nameKr: '단단한 통나무', amount: 2, icon: '🪵' };
    } else if (rType === 'rock_ore') {
      yieldInfo = { itemKey: 'iron_ore', nameKr: '순도 높은 철광석', amount: 2, icon: '🪨' };
    } else if (rType === 'herb_plant') {
      yieldInfo = { itemKey: 'healing_herb', nameKr: '신선한 치유초', amount: 3, icon: '🌿' };
    } else if (rType === 'campfire') {
      yieldInfo = { itemKey: 'cooked_ration', nameKr: '구운 야영지 비상식량', amount: 1, icon: '🍖' };
    }

    // Deduct node durability (HP)
    const damage = 50;
    node.resources.hp = Math.max(0, node.resources.hp - damage);

    // Add harvested item directly into Harvester's 20-Slot Inventory & train life skill!
    const addedToInv = addItemToInventory(harvester, {
      id: `${yieldInfo.itemKey}_${Date.now()}`,
      name: yieldInfo.nameKr,
      icon: yieldInfo.icon,
      category: rType === 'campfire' ? 'consumable' : 'material',
      rarity: 'common',
      quantity: yieldInfo.amount,
      maxStack: 50,
      weightKg: rType === 'rock_ore' ? 1.2 : rType === 'tree' ? 0.8 : 0.2,
      valueGold: 50, // 50 코퍼
      description: `솔라리스 언덕 [${node.name}]에서 채집한 신선한 자원입니다.`,
    });

    if (rType === 'tree') practiceSelfSkill(harvester, 'woodworking', 0.6);
    else if (rType === 'rock_ore') practiceSelfSkill(harvester, 'metalworking', 0.6);
    else if (rType === 'herb_plant') practiceSelfSkill(harvester, 'alchemy', 0.6);
    else if (rType === 'campfire') practiceSelfSkill(harvester, 'cooking', 0.6);

    // Emit event
    EventBus.emit('RESOURCE_GATHERED', {
      harvesterId: harvester.id,
      resourceType: yieldInfo.nameKr,
      amount: yieldInfo.amount,
    });

    EventBus.emit('LOG_EMITTED', {
      id: Math.random().toString(),
      timestamp: performance.now(),
      text: `🎉 [채집 성공] [${node.name}] 에서 [${yieldInfo.icon} ${yieldInfo.nameKr} x${yieldInfo.amount}] 획득! (${harvester.name} 20칸 인벤토리 ${addedToInv ? '보관 완료' : '가득 참!'})`,
      category: 'crafting',
    });

    // Node depletion check
    if (node.resources.hp <= 0) {
      node.isDead = true;
      node.respawnRemaining = 8.0;
      node.respawnTotal = 8.0;
      EventBus.emit('LOG_EMITTED', {
        id: Math.random().toString(),
        timestamp: performance.now(),
        text: `🪓 [${node.name}] 자원이 완전히 고갈되었습니다. (재생성 대기 8초)`,
        category: 'system',
      });
      this.respawnQueue.push({
        nodeId: node.id,
        respawnTimer: 8.0, // Quick 8.0s respawn for lively gameplay
        maxHp: node.resources.maxHp,
      });
    }
  }
}
